import React, { useState } from "react";
import { Card, CardContent, CardMedia, Typography, Box, Modal, IconButton } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";

const ProductCard = ({ product }) => {
	const [open, setOpen] = useState(false);
	const [showZoomIcon, setShowZoomIcon] = useState(false);

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	// Format numbers with commas but no decimals
	const formatCurrency = (value) => {
		if (!value || value === "N/A") return "N/A";
		// Remove any existing commas and handle string "N/A"
		const cleanValue = value.toString().replace(/,/g, "");
		// Parse the number and round it
		const numValue = Math.round(parseFloat(cleanValue));
		// Check if it's a valid number
		if (isNaN(numValue)) return "N/A";
		// Format with commas but no decimals
		return numValue.toLocaleString("en-IN");
	};

	const formatNumber = (num) => {
		if (!num) return "0";
		return Math.round(Number(num)).toLocaleString("en-IN");
	};

	return (
		<>
			<Card
				sx={{
					height: "100%",
					display: "flex",
					flexDirection: "column",
					borderRadius: 0,
					boxShadow: "none",
					border: "none",
					"& .MuiCardMedia-root": {
						borderRadius: 0,
					},
				}}>
				<Box
					sx={{
						position: "relative",
						"&:hover .zoom-icon": {
							opacity: 1,
						},
					}}
					onMouseEnter={() => setShowZoomIcon(true)}
					onMouseLeave={() => setShowZoomIcon(false)}>
					<CardMedia
						component="img"
						height="300"
						image={product.image_url || "placeholder.jpg"}
						alt={product.PKEY}
						onClick={handleOpen}
						sx={{
							objectFit: "cover",
							borderRadius: 0,
							cursor: "pointer",
							objectPosition: "top",
						}}
					/>
					<IconButton
						className="zoom-icon"
						onClick={handleOpen}
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							backgroundColor: "rgba(0, 0, 0, 0.5)",
							color: "white",
							opacity: 0,
							transition: "opacity 0.3s ease",
							"&:hover": {
								backgroundColor: "rgba(0, 0, 0, 0.7)",
							},
						}}>
						<ZoomInIcon />
					</IconButton>
				</Box>
				<CardContent
					sx={{
						p: 1.5,
						"&:last-child": { pb: 1.5 },
						borderRadius: 0,
					}}>
					<Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}>
						OPTION: {product.PKEY}
					</Typography>

					<Box sx={{ display: "flex", gap: 2, fontSize: "0.875rem" }}>
						{/* Left Column */}
						<Box sx={{ flex: 1 }}>
							<Typography variant="body2" sx={{ mb: 0.75 }}>
								<Box component="span" sx={{ fontWeight: "bold" }}>
									Season:
								</Box>{" "}
								{product.season || "N/A"}
							</Typography>
							<Typography variant="body2" sx={{ mb: 0.75 }}>
								<Box component="span" sx={{ fontWeight: "bold" }}>
									MRP:
								</Box>{" "}
								{formatCurrency(product.troco_ls?.mrp)}
							</Typography>
							<Box sx={{ my: 1 }}> {/* Reduced Spacer */} </Box>
							<Typography variant="body2" sx={{ mb: 0.75 }}>
								<Box component="span" sx={{ fontWeight: "bold" }}>
									T.INW:{" "}
								</Box>{" "}
								{formatNumber(product.troco_ls?.t_inw)}
							</Typography>
							<Typography variant="body2" sx={{ mb: 0.75 }}>
								<Box component="span" sx={{ fontWeight: "bold" }}>
									T.SALE:
								</Box>{" "}
								{formatNumber(product.troco_ls?.t_sale)}
							</Typography>
							<Typography variant="body2">
								<Box component="span" sx={{ fontWeight: "bold" }}>
									ST:
								</Box>{" "}
								{((product.troco_ls?.t_sale / product.troco_ls?.t_buy) * 100 || 0).toFixed(2)}%
							</Typography>
						</Box>

						{/* Right Column */}
						<Box sx={{ flex: 1 }}>
							<Typography variant="body2" sx={{ mb: 0.75 }}>
								<Box component="span" sx={{ fontWeight: "bold" }}>
									BLEND:
								</Box>{" "}
								{product.blend || "N/A"}
							</Typography>
							<Typography variant="body2" sx={{ mb: 0.75 }}>
								<Box component="span" sx={{ fontWeight: "bold" }}>
									COGS:
								</Box>{" "}
								{formatCurrency(product.COGS)}
							</Typography>
							<Box sx={{ my: 1 }}> {/* Reduced Spacer */} </Box>
							<Typography variant="body2" sx={{ mb: 0.75 }}>
								<Box component="span" sx={{ fontWeight: "bold" }}>
									GP%:
								</Box>{" "}
								{product.lcogs?.gp_percentage || "0.00%"}
							</Typography>
							<Typography variant="body2" sx={{ mb: 0.75 }}>
								GP Value: {product.lcogs?.gp_value || 0}
							</Typography>
							<Typography variant="body2" sx={{ mb: 0.75 }}>
								Online Cont: {product.lcogs?.oln_cont || "0%"}
							</Typography>
							<Typography variant="body2" sx={{ mb: 0.75 }}>
								Offline Cont: {product.lcogs?.offln_cont || "0%"}
							</Typography>
							<Typography variant="body2">
								<Box component="span" sx={{ fontWeight: "bold" }}>
									WH SOH:
								</Box>{" "}
								{formatNumber(product.INV)}
							</Typography>
						</Box>
					</Box>
				</CardContent>
			</Card>

			{/* Zoom Modal */}
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="image-modal"
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					"& .MuiBackdrop-root": {
						backgroundColor: "rgba(0, 0, 0, 0.9)",
					},
				}}>
				<Box
					sx={{
						maxWidth: "90vw",
						maxHeight: "90vh",
						outline: "none",
						bgcolor: "background.paper",
						p: 1,
					}}
					onClick={handleClose}>
					<img
						src={product.image_url || "placeholder.jpg"}
						alt={product.PKEY}
						style={{
							width: "100%",
							height: "100%",
							maxWidth: "100%",
							maxHeight: "90vh",
							objectFit: "contain",
						}}
					/>
				</Box>
			</Modal>
		</>
	);
};

export default ProductCard;
